import type { ActivityEventOf } from "@solution-advisor/context-events";
import { useCallback, useEffect } from "react";

import { useActivityTracker } from "src/activity/ActivityProvider";
import type { TrackInput } from "src/activity/activityTracker";

/**
 * The one way feature code reports what the user did. Types come from the shared contract, so an
 * event the API would reject does not compile.
 *
 *   const track = useTrackActivity();
 *   track({ type: "document.opened", data: { documentId, title } });
 */
export const useTrackActivity = () => {
	const tracker = useActivityTracker();
	return useCallback((input: TrackInput) => tracker.track(input), [tracker]);
};

type EntityKind = ActivityEventOf<"entity.viewed">["data"]["kind"];

/** Emits `entity.viewed` once the entity has loaded (and again if the user navigates to another). */
export const useTrackEntityView = (kind: EntityKind, id: string | undefined, name: string | undefined) => {
	const track = useTrackActivity();
	useEffect(() => {
		if (id) track({ type: "entity.viewed", data: { kind, id, ...(name ? { name } : {}) } });
	}, [id, kind, name, track]);
};
